import Vue from 'vue';
import Router from 'vue-router';

import { Auth } from 'aws-amplify';
import store from '@/store';

Vue.use(Router);

const appName = 'Smartology';

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'redirect',
      redirect: {
        name: 'login',
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard'),
      meta: {
        title: `Campaigns | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        type: 'MY_DASHBOARD',
      },
    },
    {
      path: '/selfservice-dashboard',
      name: 'selfServiceDashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard'),
      meta: {
        title: `Self Service Campaigns | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        type: 'SELF_SERVICE_DASHBOARD',
      },
    },
    {
      path: '/shared',
      name: 'shareDashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/Share/ShareDashboard'),
      meta: {
        title: `Shared Campaigns | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/reports',
      name: 'reportsGeneratorDashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/ReportsGenerator/ReportsGenerator'),
      meta: {
        title: `Reports | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/on-boarding',
      name: 'onBoarding',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/authentication/OnBoarding'),
      meta: {
        title: `User details | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */ '@/views/authentication/Login'),
      meta: {
        title: `Sign in | My ${appName}`,
      },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/Register'),
      meta: {
        title: `Register account | My ${appName}`,
      },
    },
    {
      path: '/confirm',
      name: 'confirm',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/Confirm'),
      meta: {
        title: `Confirm account | My ${appName}`,
      },
    },
    {
      path: '/verify',
      name: 'verify',
      component: () => import(/* webpackChunkName: "login" */ '@/views/authentication/Verify'),
      meta: {
        title: `Verify | My ${appName}`,
      },
    },
    {
      path: '/forgot-password',
      name: 'forgotPassword',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/ForgotPassword'),
      meta: {
        title: `Forgotten password | My ${appName}`,
      },
    },
    {
      path: '/verify-forgotten-password',
      name: 'verifyForgottenPassword',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/VerifyForgottenPassword'),
      meta: {
        title: `Verify forgotten password | My ${appName}`,
      },
    },
    {
      path: '/confirm-forgotten-password',
      name: 'confirmForgottenPassword',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/ForgotPasswordConfirm'),
      meta: {
        title: `Confirm forgotten password | My ${appName}`,
      },
    },
    {
      path: '/new-password',
      name: 'newPasswordRequired',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/NewPasswordRequired'),
      meta: {
        title: `Submit new password | My ${appName}`,
      },
    },
    {
      path: '/finish-account-setup',
      name: 'finishAccountSetup',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/authentication/FinishAccountSetup'),
      meta: {
        title: `Submit new password | My ${appName}`,
      },
    },
    {
      path: '/share',
      name: 'ShareRouter',
      component: () => import(/* webpackChunkName: "authentication" */ '@/views/Share/ShareRouter'),
      meta: {
        title: `Share  | My ${appName}`,
        requiresAuth: false,
      },
    },
    {
      path: '/new-campaign',
      name: 'NewCampaignBlank',
      component: () => import(/* webpackChunkName: "campaign" */ '@/views/Campaign/NewCampaign'),
      meta: {
        title: `Add your campaign | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/new-campaign/aid/:advertiserId/cid/:campaignId?',
      name: 'NewCampaign',
      component: () => import(/* webpackChunkName: "campaign" */ '@/views/Campaign/NewCampaign'),
      meta: {
        title: `Add your campaign | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'CAMPAIGN',
      },
    },
    {
      path: '/edit-campaign/aid/:advertiserId/cid/:campaignId',
      name: 'EditCampaign',
      component: () => import(/* webpackChunkName: "campaign" */ '@/views/Campaign/EditCampaign'),
      meta: {
        title: `Edit your campaign | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'CAMPAIGN',
      },
    },
    {
      path: '/view-campaign/aid/:advertiserId/cid/:campaignId',
      name: 'ViewCampaign',
      component: () => import(/* webpackChunkName: "campaign" */ '@/views/Campaign/EditCampaign'),
      meta: {
        title: `View your campaign | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'CAMPAIGN',
      },
    },
    {
      path: '/aid/:advertiserId/cid/:campaignId/fid/:feedId/article/:articleId',
      name: 'ContentEditor',
      component: () => import(/* webpackChunkName: "campaign" */ '@/modules/content/components/ContentPage/ContentEditorPage'),
      meta: {
        title: `Add your campaign | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/campaign-matches/aid/:advertiserId/cid/:campaignId',
      name: 'CampaignMatches',
      component: () => import(/* webpackChunkName: "reporting" */ '@/views/CampaignMatches/CampaignMatches.vue'),
      meta: {
        title: `Campaign Matches | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/showcase/',
      name: 'ShowcaseIndex',
      redirect: {
        name: 'dashboard',
      },
    },
    {
      path: '/cid/:campaignId?/showcase',
      name: 'Campaign Showcase',
      component: () => import(/* webpackChunkName: "showcase" */ '@/views/Showcase/CampaignView'),
      meta: {
        title: `Campaign showcase | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'SHOWCASE',
      },
    },
    {
      path: '/billing',
      name: 'billing',
      component: () => import(/* webpackChunkName: "billing" */ '@/views/Billing/Index'),
      meta: {
        title: `Billing | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/templates',
      name: 'templates',
      component: () => import(/* webpackChunkName: "templates" */ '@/views/Templates/Templates'),
      meta: {
        title: `Templates | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
      },
    },
    {
      path: '/templates/create',
      name: 'TemplateCreate',
      component: () => import(/* webpackChunkName: "templates" */ '@/views/Templates/TemplateForm'),
      meta: {
        title: 'Create template',
      },
    },
    {
      path: '/templates/update/:id',
      name: 'TemplateUpdate',
      component: () => import(/* webpackChunkName: "templates" */ '@/views/Templates/TemplateForm'),
      meta: {
        title: 'Edit template',
      },
    },
    {
      path: '/settings',
      name: 'AccountSettings',
      component: () => import(/* webpackChunkName: "campaign" */ '@/views/AccountSettings'),
      meta: {
        title: 'Account Settings',
        requiresAuth: true,
        fullHeight: true,
        meta: { hidden: true },
      },
    },
    {
      path: '/reports/aid/:advertiserId/group/:group',
      name: 'GroupReportingApp',
      component: () => import(/* webpackChunkName: "reporting" */ '@/views/Reporting/ReportingApp'),
      meta: {
        title: `Group Reporting | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'REPORTING',
      },
    },
    {
      path: '/reports/aid/:advertiserId/cid/:campaignId',
      name: 'ReportingApp',
      component: () => import(/* webpackChunkName: "reporting" */ '@/views/Reporting/ReportingApp'),
      meta: {
        title: `Reporting | My ${appName}`,
        requiresAuth: true,
        fullHeight: true,
        campaignAccessType: 'REPORTING',
      },
    },
    {
      path: '*',
      component: () => import(/* webpackChunkName: "dashboard" */ '@/views/NotFoundComponent'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);

  if (nearestWithTitle) document.title = `${nearestWithTitle.meta.title}`;

  if (to.meta.campaignAccessType && to.params.campaignId) {
    await Auth.currentAuthenticatedUser()
      .then((response) => {
        const roles = response.signInUserSession.accessToken.payload['cognito:groups'];

        store.dispatch('campaigns/GET_ACCESS', to.params.campaignId).then((accessResponse) => {
          const hasShareAccess = accessResponse.shareTypes.includes(to.meta.campaignAccessType) || accessResponse.shareTypes.includes('CAMPAIGN');
          if ((!accessResponse.ownerAccess && !hasShareAccess) && !roles.includes('sys_admin')) {
            store.commit('SET_GLOBAL_MESSAGE', {
              type: 'danger',
              message: `You do not have access to the "${accessResponse.name}" campaign, please contact the campaign owner.`,
            });
            next('/dashboard');
          } else {
            next();
          }
        });
      })
      .catch(() => next());
  } else {
    next();
  }
});

router.beforeResolve(async (to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    try {
      await Auth.currentAuthenticatedUser()
        .then((response) => {
          const roles = response.signInUserSession.accessToken.payload['cognito:groups'];
          store.commit('authentication/SET_USER_ROLES', roles);
        })
        .catch(() => {
          next({
            path: '/',
            query: {
              redirect: to.fullPath,
            },
          });
        });
      next();
    } catch (e) {
      next({
        path: '/',
        query: {
          redirect: to.fullPath,
        },
      });
    }
  }
  next();
});

export default router;
